import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { useTheme } from './hooks/useTheme';

import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';

import HomePage from './pages/HomePage';
import ProjectPage from './pages/ProjectPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import FAQPage from './pages/FAQPage';
import BookingPage from './pages/BookingPage';
import NotFoundPage from './pages/NotFoundPage';

import lightLogo from './assets/light-theme-logo.png';
import darkLogo from './assets/dark-theme-logo.png';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      // Use 'instant' to override html { scroll-behavior: smooth } — route changes
      // should not animate; smooth would make it look like an unexpected page scroll.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const id = hash.replace('#', '');
    let previousTop = null;
    let stableCount = 0;
    let userScrolled = false;

    const onUserScroll = () => {
      userScrolled = true;
    };
    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchmove', onUserScroll, { passive: true });

    const interval = setInterval(() => {
      if (userScrolled) {
        clearInterval(interval);
        return;
      }

      const el = document.getElementById(id);
      if (!el) return;

      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      if (top === previousTop) {
        stableCount++;
      } else {
        stableCount = 0;
        previousTop = top;
      }

      // Wait for position to be stable for 2 consecutive checks (~300ms)
      if (stableCount >= 2) {
        clearInterval(interval);
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);

    // Hard stop after 2s — scroll wherever the element is by then
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!userScrolled) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchmove', onUserScroll);
    };
  }, [pathname, hash]);

  return null;
};

const AppShell = () => {
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const logoSrc = theme === 'dark' ? darkLogo : lightLogo;

  const handleNavClick = (e) => {
    if (e.target.tagName === 'A') setIsOpen(false);
  };

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <div className="app-shell">
        <a href="#main" className="skip-link">
          Skip to main content
        </a>

        <Navbar
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          theme={theme}
          toggleTheme={toggleTheme}
          logoSrc={logoSrc}
          handleNavClick={handleNavClick}
          isScrolled={isScrolled}
        />

        <main id="main">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/projects/:id" element={<ProjectPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route
                path="/terms-of-service"
                element={<TermsOfServicePage />}
              />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ErrorBoundary>
        </main>

        <Footer logoSrc={darkLogo} />
        <CookieBanner />
      </div>
    </ErrorBoundary>
  );
};

const App = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default App;
